<template>
  <div class="camera-capture">
    <!-- Modal para captura de cámara -->
    <div class="modal" :class="{ 'is-active': isModalOpen }">
      <div class="modal-background" @click="closeCamera"></div>
      <div class="modal-card">
        <header class="modal-card-head">
          <p class="modal-card-title">
            <span class="icon">
              <i class="fa fa-camera"></i>
            </span>
            {{ $t("capture photo") }}
          </p>
          <button
            class="delete"
            aria-label="close"
            @click="closeCamera"
          ></button>
        </header>
        <section class="modal-card-body">
          <div class="camera-container">
            <!-- Video preview -->
            <video
              v-show="!capturedImage && !errorMessage"
              ref="video"
              autoplay
              playsinline
              class="camera-video"
            ></video>

            <!-- Canvas para capturar -->
            <canvas ref="canvas" style="display: none"></canvas>

            <!-- Imagen capturada -->
            <div v-if="capturedImage" class="captured-image-container">
              <img :src="capturedImage" alt="Captured" class="captured-image" />
            </div>

            <!-- Mensaje de error -->
            <div v-if="errorMessage" class="notification is-danger">
              <span class="icon">
                <i class="fa fa-exclamation-triangle"></i>
              </span>
              {{ errorMessage }}
            </div>

            <!-- Loading -->
            <div v-if="isLoading" class="has-text-centered">
              <span class="icon is-large">
                <i class="fa fa-spinner fa-spin"></i>
              </span>
              <p>{{ $t("initializing camera") }}...</p>
            </div>
          </div>
        </section>
        <footer class="modal-card-foot">
          <div class="buttons is-centered is-fullwidth">
            <!-- Botón para capturar -->
            <button
              v-if="!capturedImage && !errorMessage"
              class="button is-primary is-large"
              @click="capturePhoto"
              :disabled="isLoading"
            >
              <span class="icon">
                <i class="fa fa-camera"></i>
              </span>
              <span>{{ $t("take photo") }}</span>
            </button>

            <!-- Botones después de capturar -->
            <template v-if="capturedImage">
              <button class="button is-success is-large" @click="acceptPhoto">
                <span class="icon">
                  <i class="fa fa-check"></i>
                </span>
                <span>{{ $t("accept") }}</span>
              </button>
              <button class="button is-light is-large" @click="retakePhoto">
                <span class="icon">
                  <i class="fa fa-refresh"></i>
                </span>
                <span>{{ $t("retake") }}</span>
              </button>
            </template>

            <!-- Botón cancelar -->
            <button class="button is-light is-large" @click="closeCamera">
              <span class="icon">
                <i class="fa fa-times"></i>
              </span>
              <span>{{ $t("cancel") }}</span>
            </button>
          </div>
        </footer>
      </div>
    </div>

    <!-- Botón para abrir cámara -->
    <div class="field">
      <div class="buttons is-centered">
        <button type="button" class="button is-info" @click="openCamera">
          <span class="icon">
            <i class="fa fa-camera"></i>
          </span>
          <span>{{ $t("take photo") }}</span>
        </button>

        <!-- <span class="mx-2">{{ $t("or") }}</span> -->

        <div class="file">
          <label class="file-label">
            <input
              class="file-input"
              type="file"
              accept="image/*"
              @change="handleFileUpload"
            />
            <span class="file-cta">
              <span class="file-icon">
                <i class="fa fa-upload"></i>
              </span>
              <span class="file-label">{{ $t("or upload file") }}</span>
            </span>
          </label>
        </div>
      </div>
    </div>

    <!-- Preview de la imagen -->
    <div v-if="imagePreview" class="field">
      <div class="has-text-centered">
        <img :src="imagePreview" :alt="$t('preview')" class="preview-image" />
        <div class="mt-2">
          <button class="button is-small is-danger" @click="removeImage">
            <span class="icon">
              <i class="fa fa-trash"></i>
            </span>
            <span>{{ $t("remove") }}</span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "CameraCapture",
  props: {
    fieldName: {
      type: String,
      required: true,
    },
    imagePreview: {
      type: String,
      default: null,
    },
  },
  emits: ["photo-captured", "file-uploaded"],
  data() {
    return {
      isModalOpen: false,
      isLoading: false,
      stream: null,
      capturedImage: null,
      errorMessage: null,
    };
  },
  methods: {
    async openCamera() {
      this.isModalOpen = true;
      this.isLoading = true;
      this.errorMessage = null;
      this.capturedImage = null;

      try {
        // Solicitar acceso a la cámara
        this.stream = await navigator.mediaDevices.getUserMedia({
          video: {
            facingMode: "environment", // Cámara trasera por defecto
            width: { ideal: 1280 },
            height: { ideal: 720 },
          },
        });

        // Asignar el stream al video
        this.$nextTick(() => {
          if (this.$refs.video) {
            this.$refs.video.srcObject = this.stream;
            this.isLoading = false;
          }
        });
      } catch (error) {
        console.error("Error accessing camera:", error);
        this.errorMessage = this.$t("camera access denied");
        this.isLoading = false;
      }
    },

    closeCamera() {
      this.isModalOpen = false;
      this.stopCamera();
      this.capturedImage = null;
      this.errorMessage = null;
    },

    stopCamera() {
      if (this.stream) {
        this.stream.getTracks().forEach((track) => track.stop());
        this.stream = null;
      }
    },

    capturePhoto() {
      const video = this.$refs.video;
      const canvas = this.$refs.canvas;
      const context = canvas.getContext("2d");

      // Configurar el canvas con las dimensiones del video
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;

      // Dibujar el frame actual del video en el canvas
      context.drawImage(video, 0, 0, canvas.width, canvas.height);

      // Convertir a base64
      this.capturedImage = canvas.toDataURL("image/jpeg", 0.8);

      // Detener la cámara
      this.stopCamera();
    },

    retakePhoto() {
      this.capturedImage = null;
      this.openCamera();
    },

    acceptPhoto() {
      // Convertir base64 a File
      const byteString = atob(this.capturedImage.split(",")[1]);
      const arrayBuffer = new ArrayBuffer(byteString.length);
      const uint8Array = new Uint8Array(arrayBuffer);

      for (let i = 0; i < byteString.length; i++) {
        uint8Array[i] = byteString.charCodeAt(i);
      }

      const file = new File([arrayBuffer], `${this.fieldName}_captured.jpg`, {
        type: "image/jpeg",
      });

      // Emitir el evento con el archivo
      this.$emit("photo-captured", {
        fieldName: this.fieldName,
        file: file,
        preview: this.capturedImage,
      });

      this.closeCamera();
    },

    handleFileUpload(event) {
      const file = event.target.files[0];
      if (file) {
        this.$emit("file-uploaded", {
          fieldName: this.fieldName,
          file: file,
        });
      }
    },

    removeImage() {
      this.$emit("photo-captured", {
        fieldName: this.fieldName,
        file: null,
        preview: null,
      });
    },
  },

  beforeUnmount() {
    this.stopCamera();
  },
};
</script>

<style scoped>
.camera-capture {
  margin-bottom: 1rem;
}

.camera-video {
  width: 100%;
  max-width: 500px;
  height: auto;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.captured-image-container {
  text-align: center;
}

.captured-image {
  max-width: 100%;
  max-height: 400px;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.preview-image {
  max-width: 200px;
  max-height: 200px;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.camera-container {
  text-align: center;
  min-height: 200px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
}

.modal-card {
  max-width: 90vw;
  max-height: 90vh;
}

.modal-card-body {
  max-height: 60vh;
  overflow-y: auto;
}

.buttons.is-fullwidth {
  width: 100%;
  justify-content: center;
}

.buttons.is-fullwidth .button {
  margin: 0.25rem;
}

@media screen and (max-width: 768px) {
  .modal-card {
    max-width: 95vw;
    margin: 1rem;
  }

  .buttons.is-fullwidth .button {
    flex: 1;
    margin: 0.125rem;
  }
}
</style>
